import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import {
    SubmittalJobSummary,
    SubmittalJobSummaryWithNewStatuses
} from 'src/app/shared/models/submittal-job-summary.model';
import { MatLegacySelectChange as MatSelectChange } from '@angular/material/legacy-select';
import { SubmittalStatusWithLegacyTranslationDto } from '../../models/submittal-status.model';
import { SubmittalStatusReason } from 'src/app/shared/models/submittal-status-reason.model';
import { SubmittalStatusRequestors } from '../../enums/submittal-status-requestors.enum';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';
import { BehaviorSubject } from 'rxjs';
import { SubmittalStatusReasonFilterPipe } from '../../pipes/submittal-status-reason-filter.pipe';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

@UntilDestroy()
@Component({
    selector: 'nova-submittal-status-dropdown',
    templateUrl: './submittal-status-dropdown.component.html',
    styleUrls: ['./submittal-status-dropdown.component.scss']
})
export class SubmittalStatusDropdownComponent implements OnChanges {
    @Input() requestor: SubmittalStatusRequestors = SubmittalStatusRequestors.StaffRecruiting;
    @Input() submittal: SubmittalJobSummaryWithNewStatuses;
    @Output() onUpdateSubmittalStatus = new EventEmitter();

    @Input() statuses: BehaviorSubject<Array<SubmittalStatusWithLegacyTranslationDto>>;
    @Input() reasons: BehaviorSubject<Array<SubmittalStatusReason>>;

    selectedSubmittal: UntypedFormControl = null;
    selectedReason: UntypedFormControl = null;
    dateStarted: UntypedFormControl = null;
    SubmittalStatusRequestors = SubmittalStatusRequestors;
    isReady = false;
    hasReasons = false;
    isStartedStatusSelected = false;

    constructor(private readonly submittalStatusReasonFilter: SubmittalStatusReasonFilterPipe) {
        this.selectedSubmittal = new UntypedFormControl();
        this.selectedReason = new UntypedFormControl();
        this.dateStarted = new UntypedFormControl();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (
            changes &&
            changes.submittal?.currentValue &&
            changes.statuses?.currentValue &&
            changes.reasons?.currentValue
        ) {
            this.getObs();
            this.isReady = true;
        }
    }

    refreshSubmittal(submittal: SubmittalJobSummary = this.submittal): void {
        this.runChecks();
        this.selectedSubmittal.setValue(submittal?.submittalStatusId);
        this.hasReasons && this.selectedReason.setValue(submittal?.submittalStatusReasonId || null);
        this.dateStarted.setValue(submittal?.confirmedStartDate);
    }

    handleOnUpdateSubmittalStatus(event: MatSelectChange): void {
        this.runChecks();
        this.submittal.confirmedStartDate = null;
        this.submittal.submittalStatusReasonId = null;
        this.submittal.submittalStatusId = event.value;

        const submittalStatus: SubmittalStatusWithLegacyTranslationDto | undefined = this.statuses.value.find(
            (status: SubmittalStatusWithLegacyTranslationDto) => status.id === event.value
        );
        if (submittalStatus?.newPermSubmittalStatusId) {
            this.submittal.newPermSubmittalStatusId = submittalStatus.newPermSubmittalStatusId;
            this.submittal.newPermSubmittalSubStatusId = submittalStatus.newPermSubmittalSubStatusId;
        }

        this.submittal.submittalStatusReasonChangeOnly = false;
        this.onUpdateSubmittalStatus.emit(this.submittal);
        this.refreshSubmittal();
    }

    hanldeUpdateSubmittalStatusReason(event: MatSelectChange): void {
        this.runChecks();
        const val = event.value === 0 ? null : event.value;
        this.submittal.submittalStatusReasonId = val;
        this.submittal.submittalStatusReasonChangeOnly = true;
        this.onUpdateSubmittalStatus.emit(this.submittal);
    }

    handleStartDateChange(event: MatDatepickerInputEvent<Date>): void {
        this.submittal.confirmedStartDate = event.value;
        this.submittal.submittalStatusReasonChangeOnly = false;
        this.onUpdateSubmittalStatus.emit(this.submittal);
    }

    runChecks(): void {
        this.hasReasons =
            this.submittalStatusReasonFilter.transform(this.reasons.value, this.submittal?.submittalStatusId).length >
            0;
        this.isStartedStatusSelected =
            this.statuses.value.find((x) => x.name.toLowerCase() === 'started')?.id ===
            this.submittal?.submittalStatusId;
    }

    getObs(): void {
        this.statuses.pipe(untilDestroyed(this)).subscribe((statuses) => {
            statuses && this.refreshSubmittal();
        });
        this.reasons.pipe(untilDestroyed(this)).subscribe((reasons) => {
            reasons && this.refreshSubmittal();
        });
    }
}
